import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { CountryData } from '../../../../company/types/country-data';
import { IndustryData } from '../../../../company/types/industry-data';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { StateData } from '../../../../company/types/state-data';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { translateFn } from '../../../../utils/translator.helper';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import { MspInProductTrialId } from '../../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types';

export class MspInProductTrialsStartTrialPopUpWebView extends BaseWebComponentView {
  public dom: {
    $countryFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $stateFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $industryFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $startTrialInfo: HTMLElement;
    $startTrialCountryIndustryInfo: HTMLElement;
  };
  private formGroup: FormGroup;
  private states: StateData[];
  private context: MspInProductTrialId;

  getDomElements(): void {
    this.dom = {
      $countryFormField: this.shadow.querySelector('#msp-country-form-field'),
      $stateFormField: this.shadow.querySelector('#msp-state-form-field'),
      $industryFormField: this.shadow.querySelector('#msp-industry-form-field'),
      $startTrialInfo: this.shadow.querySelector('#start-trial-info'),
      $startTrialCountryIndustryInfo: this.shadow.querySelector('#start-trial-country-industry-info'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    if (this.dom.$countryFormField.Input) {
      this.dom.$countryFormField.Input.placeholder = translateFn('Select the country');
      this.dom.$countryFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
    }

    if (this.dom.$industryFormField.Input) {
      this.dom.$industryFormField.Input.placeholder = translateFn('Select industry');
      this.dom.$industryFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
    }
  }

  disconnected() {}

  buildSuccessView(viewData) {
    this.setStartTrialInfoMessage();

    this.states = viewData.states;
    this.createFormGroup();
    if (this.dom.$countryFormField) {
      this.setCountryDropdownOptions(viewData.countries);
      this.dom.$countryFormField.Input.addEventListener('selectionChanged', this.onCountryDropdownChange.bind(this));
    }
    if (this.dom.$industryFormField) {
      this.setIndustryDropdownOptions(viewData.industries);
    }
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  public setFormGroupValues(formGroupValue: FormGroupValue): void {
    this.formGroup.value = formGroupValue;
    this.setFieldsOptionValue();
  }

  public setContext(context: MspInProductTrialId) {
    this.context = context;
  }
  private setFieldsOptionValue() {
    this.dom.$industryFormField.Input.value = this.dom.$industryFormField.Input.selectedOption?.value ?? '';
    this.dom.$countryFormField.Input.value = this.dom.$countryFormField.Input.selectedOption?.value ?? '';
    this.dom.$stateFormField.Input.value = this.dom.$stateFormField.Input.selectedOption?.value ?? '';
  }

  private onCountryDropdownChange(): void {
    if (this.states) {
      const filteredStates = this.states.filter(state => {
        return state.countryCode === this.dom.$countryFormField.value;
      });

      if (filteredStates.length > 0) {
        this.dom.$stateFormField.classList.remove('hidden');
        this.dom.$stateFormField.Input.placeholder = translateFn('Select the state');
        this.dom.$stateFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
        this.dom.$stateFormField.Input.options = filteredStates.map(state => {
          return {
            title: state.stateName,
            value: state.stateCode,
          };
        });

        this.formGroup.addFormChild(['state'], this.dom.$stateFormField);
      } else {
        toggleVisibility(this.dom.$stateFormField, false);
        this.formGroup.removeFormChild(['state']);
      }

      this.dom.$stateFormField.value = '';
      this.dom.$stateFormField.Input.clearInput();
    }
  }

  private setCountryDropdownOptions(countries: CountryData[]) {
    this.dom.$countryFormField.Input.placeholder = translateFn('Select the country');
    this.dom.$countryFormField.Input.options = countries.map(country => {
      return {
        title: country.countryName,
        value: country.countryCode,
      };
    });
  }

  private setIndustryDropdownOptions(industries: IndustryData[]) {
    this.dom.$industryFormField.Input.placeholder = translateFn('Select industry');
    this.dom.$industryFormField.Input.options = industries.map(industry => {
      return {
        title: industry.name,
        value: industry.value.toString(),
      };
    });
  }

  private createFormGroup() {
    this.dom.$countryFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$stateFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$industryFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.formGroup = FormGroup.build({
      country: this.dom.$countryFormField,
      industry: this.dom.$industryFormField,
    });
  }

  private setStartTrialInfoMessage() {
    if (this.context === MspInProductTrialId.EdrTrial) {
      this.dom.$startTrialInfo.innerText =
        translateFn(
          'The EDR add-on will be enabled for the target company. The feature requires the EDR Sensor module to be deployed in order to work on a specific endpoint.'
        ) +
        '\n\n' +
        translateFn("A Reconfigure agent task will be created to deploy the module on all the company's endpoints.");
    }
    if (this.context === MspInProductTrialId.MdrTrial) {
      this.dom.$startTrialInfo.innerText =
        translateFn(
          'The MDR service and associated add-ons will be enabled for the target company. The feature requires the EDR Sensor module to be deployed in order to work on a specific endpoint.'
        ) +
        '\n\n' +
        translateFn("A Reconfigure agent task will be created to deploy the module on all the company's endpoints.");
    }
    this.dom.$startTrialCountryIndustryInfo.innerText = translateFn(
      'Please confirm the following details for the target company. An accurate selection allows us to contextualize threat information and provide you with benchmarking industry context.'
    );
  }
}
